<template>
  <div
    class="teaser big"
    :class="{ visible: ivisible }"
    :style="`--i: ${stagger};`">
      <div class="image">
        <img ref="image" :src="image" :class="{ wide: wide }" :alt="title" @load="handleImageLoaded">
      </div>
      <div class="content">
        <!-- <span v-if="date">{{ date | localizeDate }}</span> -->
        <h3>{{ title }}</h3>
        <p v-html="short" />
        <CTA v-if="!noDetails && hasContent" :to="url">{{ $t('labels.more') }}</CTA>
      </div>
  </div>
</template>

<script>
  import CTA from '@/components/ui/CTA';
  import Format from '@/utils/Format';

  export default {
    name: 'TeaserBig',
    components: {
      CTA,
    },
    //---------------------------------------------------
    //
    //  Properties
    //
    //---------------------------------------------------
    props: {
      item: {
        type: Object,
        default: null,
      },
      slugPath: {
        type: String,
        default: '/',
      },
      stagger: {
        type: Number,
        default: 1,
      },
      visible: {
        type: Boolean,
        default: false,
      },
      noDetails: {
        type: Boolean,
        default: false,
      },
    },
    //---------------------------------------------------
    //
    //  Data model
    //
    //---------------------------------------------------
    data() {
      return {
        wide: false,
        ivisible: false,
      };
    },
    //---------------------------------------------------
    //
    //  Computed Properties
    //
    //---------------------------------------------------
    computed: {
      date() {
        const item = this.item;
        if (item?.created) {
          return parseInt(item.created, 10) * 1000;
        }
        return null;
      },
      title() {
        const item = this.item;
        if (item?.title) {
          return item.title;
        }
        return null;
      },
      short() {
        const item = this.item;
        if (item?.short) {
          return item.short;
        }
        return null;
      },
      hasContent() {
        return (this.item?.blocks || '[]') !== '[]';
      },
      url() {
        const item = this.item;
        if (item?.slug) {
          return `${this.slugPath}/${item.slug}/`;
        }
        return null;
      },
      image() {
        const item = this.item;
        if (item?.featured) {
          return `${this.$b.contentUrl}/${item.featured[0]?.full || item.featured[0]?.path}`;
        }
        return null;
      },
    },
    //---------------------------------------------------
    //
    //  Watch Properties
    //
    //---------------------------------------------------
    watch: {
      visible(val) {
        this.ivisible = val;
      },
    },
    //---------------------------------------------------
    //
    //  Filter Properties
    //
    //---------------------------------------------------
    filters: {
      localizeDate(value = null) {
        return Format.toLocalizedDate(new Date(value));
      },
    },
    //---------------------------------------------------
    //
    //  Validation Properties
    //
    //---------------------------------------------------
    // validations: {},
    //---------------------------------------------------
    //
    //  Vue Lifecycle
    //
    //---------------------------------------------------
    // beforeCreate() {},
    // created() {},
    // beforeMount() {},
    // render(h) { return h(); },
    mounted() {
      const { image } = this.$refs;
      if (image && image.naturalWidth > 0 && image.naturalHeight > 0) {
        this.handleImageLoaded();
      }
    },
    // beforeUpdate() {},
    // updated() {},
    // beforeDestroy() {},
    // destroyed() {},
    //---------------------------------------------------
    //
    //  Methods
    //
    //---------------------------------------------------
    methods: {
      handleImageLoaded() {
        const { image } = this.$refs;
        if (image.naturalWidth / image.naturalHeight >= 1) {
          this.wide = true;
        }
        if (!this.$listeners?.loaded) {
          this.ivisible = true;
        }
        this.$emit('loaded');
      },
    },
  };
</script>

<style lang="scss">
@import '~scss/mixins';
@import '~scss/variables';

.teaser.big {
  display: flex;
  width: calc(100% - 40px);
  margin-left: 20px;
  gap: 20px;
  transform: translate(-5%, 0);
  opacity: 0;

  @include screen-mobile {
    flex-direction: column;
    gap: 0;
    margin: 80px 0;
    width: 100%;

    &:first-of-type {
      margin-top: 40px;
    }
  }

  & > div {
    position: relative;
    margin: 40px 0;

    @include screen-mobile {
      margin: 0;
    }

    &.image {
      position: relative;
      padding-bottom: 31.25%;
      width: 50%;
      max-width: 50%;
      min-width: 50%;
      text-align: right;
      font-size: 0;
      overflow: hidden;

      @include screen-mobile {
        width: 100%;
        max-width: 100%;
        min-width: 100%;
        padding-bottom: 0;
      }

      & > img {
        position: absolute;
        top: 0;
        left: auto;
        right: 0;
        width: auto;
        height: 100%;
        max-width: 100%;
        max-height: 100%;
        object-fit: contain;
        box-shadow: 0 0 5px rgba(0, 0, 0, .2);

        &.wide {
          width: 100%;
          height: auto;
        }

        @include screen-mobile {
          width: 100%;
          height: auto;
          position: relative;
          left: auto;
          right: auto;
        }
      }
    }

    &.content {
      position: relative;
      padding: 20px;
      width: 50%;
      max-width: 50%;
      min-width: 50%;
      background-color: rgba(255, 255, 255, .85);
      transform: translate(-5%, 0);
      opacity: 0;

      @include screen-mobile {
        left: auto;
        top: 0;
        padding: 20px 0;
        width: 100%;
        max-width: 100%;
        min-width: 100%;
        bottom: auto;
      }
    }
  }

  &:nth-child(even) {
    transform: translate(5%, 0);
    flex-direction: row-reverse;
    animation-name: from-right;

    @include screen-mobile {
      flex-direction: column;
    }

    & > div {
      &.content {
        text-align: right;
        left: 0;
        transform: translate(5%, 0);
        animation-name: from-right;

        @include screen-mobile {
          left: 0;
        }
      }
      &.image {
        left: auto;
        right: 0;
        text-align: left;
      }
    }
  }

  &.visible {
    animation-name: from-left;
    animation-duration: 0.5s;
    animation-delay: calc(250ms + (var(--i) * 100ms));
    animation-fill-mode: forwards;

    & > div {
      &.content {
        animation-name: from-left;
        animation-duration: 0.5s;
        animation-delay: calc(500ms + (var(--i) * 100ms));
        animation-fill-mode: forwards;
      }
    }

    &:nth-child(even) {
      animation-name: from-right;
      & > div {
        &.content {
          animation-name: from-right;
        }
      }
    }
  }
}
</style>
